import React, { useEffect, useMemo, useState } from 'react';
import CreateColumn from '../../../../Services/agGrid/createColumn';
import Commons from '../../../../Services/Common/Common';
import Global from '../../../../Services/Common/Global';
import CloseIcon from '@mui/icons-material/Close';
import {
  Box,
  Dialog,
  DialogContent,
  IconButton,
  Typography,
} from '@mui/material';

/**
 * PPE_BASIC_03(보호구 지급대장) 컬럼 정의
 * 그리드: NO(신규 행만 삭제 아이콘, 조회 행은 순번만)·부서·직위·성명·서명…
 */
/** 보호구 사용부서(managerDepartments)에 행의 userDepCd가 포함된 항목만 */
const ToDepartmentCodeSet = (raw) => {
  if (raw == null || raw === '') return new Set();
  if (Array.isArray(raw)) {
    return new Set(raw.map((v) => String(v).trim()).filter(Boolean));
  }
  return new Set(
    String(raw)
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean)
  );
};

export const FilterEquipOptionsByUserDepCd = (equipRows, userDepCd) => {
  const dep = String(userDepCd ?? '').trim();
  if (!dep || !Array.isArray(equipRows)) return [];
  return equipRows.filter((row) =>
    ToDepartmentCodeSet(row.managerDepartments).has(dep)
  );
};

/** 신규 행: 부서별 보호구 없음·성명 미선택 시 안내 (빈 콤보 대신) */
const CreateEquipNmHintCellRenderer = (equipOptionsList) => (params) => {
  const data = params?.data;
  const list = equipOptionsList || [];
  if (!data || data.newYn !== 'Y') {
    return <span>{data?.equipNm ?? ''}</span>;
  }
  const depCd = String(data.userDepCd ?? '').trim();
  const allowed = FilterEquipOptionsByUserDepCd(list, depCd);
  const hasLabel = !!(data.equipNm && String(data.equipNm).trim());

  if (hasLabel) {
    return <span>{data.equipNm}</span>;
  }
  if (!depCd) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.3 }}
      >
        성명을 선택하면 보호구를 선택할 수 있습니다.
      </Typography>
    );
  }
  if (allowed.length === 0) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ lineHeight: 1.3 }}
      >
        해당 부서에 등록된 보호구가 없습니다.
      </Typography>
    );
  }
  return <span>{data.equipNm ?? ''}</span>;
};

const BuildIssuSignImageSrc = (raw) => {
  if (raw == null || String(raw).trim() === '') {
    return '';
  }
  const normalized = String(raw).replace(/\\/g, '/');
  const enc = Commons.EncodeUrl(normalized);
  const base = String(Global.file?.useUrl ?? '').replace(/\/?$/, '/');
  return enc ? `${base}files/${enc}` : '';
};

/** issuSignPath: 그리드 썸네일 + 클릭 시 실제 이미지 확대 확인 */
const IssuSignPathCellRenderer = (params) => {
  const raw = params?.value;
  const src = useMemo(() => BuildIssuSignImageSrc(raw), [raw]);
  const [open, setOpen] = useState(false);
  const [broken, setBroken] = useState(false);

  useEffect(() => {
    setBroken(false);
    setOpen(false);
  }, [raw]);

  if (!src) {
    return '';
  }

  if (broken) {
    return (
      <Typography variant="caption" color="text.secondary">
        이미지 없음
      </Typography>
    );
  }

  return (
    <>
      <Box
        title="클릭하여 서명 이미지 크게 보기"
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          minHeight: 80,
          py: 0.5,
          cursor: 'pointer',
        }}
        onClick={(e) => {
          e.stopPropagation();
          setOpen(true);
        }}
      >
        <img
          alt="서명 미리보기"
          src={src}
          style={{
            maxHeight: 88,
            maxWidth: '100%',
            objectFit: 'contain',
            display: 'block',
          }}
          onError={() => setBroken(true)}
        />
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        maxWidth="lg"
        fullWidth
        onClick={(e) => e.stopPropagation()}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            px: 1,
            py: 0.5,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle1" sx={{ pl: 1 }}>
            서명 확인
          </Typography>
          <IconButton
            aria-label="닫기"
            onClick={() => setOpen(false)}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            minHeight: 320,
          }}
        >
          <img
            alt="서명"
            src={src}
            style={{
              maxWidth: '100%',
              maxHeight: 'min(85vh, 900px)',
              objectFit: 'contain',
            }}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export const getColumnDefs1 = (props = {}) => {
  const equipOptionsList = props.equipOptions || [];

  return [
    CreateColumn('no', 'NO', 'center', 80, false, false, false, 'checkbox', {
      checkboxProps: {
        headerCheckboxSelection: false,
        /** 조회 행: 체크박스 없이 순번만. 신규 행: 삭제 아이콘. 삭제예정 행: 복원 UI 유지 */
        hideCheckboxCondition: (params) =>
          params.data?.newYn !== 'Y' && params.data?.deleteYn !== 'Y',
        DeleteNewRow1: props.DeleteNewRow1,
        RestoreRow1: props.RestoreRow1,
        gridRef1: props.gridRef1,
        rowData1: props.rowData1,
        selectedRowNosRef1: props.selectedRowNosRef,
        lastCheckedIndexRef1: props.lastCheckedIndexRef,
        handleRowSelection: props.handleRowSelection,
      },
      maxWidth: 100,
    }),
    CreateColumn(
      'userDepNm',
      '부서',
      'left',
      140,
      false,
      false,
      false,
      'text',
      {
        minWidth: 100,
        cellClass: 'none-editable-cell',
      }
    ),
    CreateColumn(
      'userRank',
      '직위',
      'center',
      100,
      false,
      false,
      false,
      'text',
      {
        minWidth: 80,
        cellClass: 'none-editable-cell',
      }
    ),
    CreateColumn(
      'userNm',
      '성명',
      'center',
      130,
      false,
      true,
      false,
      'requiredButton',
      {
        minWidth: 100,
        buttonClickHandler: (params) => {
          if (params.data?.newYn === 'Y') {
            props.MemberSelectPopupClick1?.(params, 'userNm');
          }
        },
        buttonEmptyText: '설정하기',
        buttonEmptyTooltip: '성명(지급 대상)을 선택하세요.',
        buttonValueTooltip: (params) => {
          const isNew = params.data?.newYn === 'Y';
          return isNew
            ? '클릭하여 성명을 다시 선택할 수 있습니다.'
            : '조회된 행은 여기서 수정할 수 없습니다.';
        },
        cellClassRules: {
          'required-cell': (params) => {
            if (params.data?.newYn !== 'Y') return false;
            const value = params.value;
            if (typeof value === 'string') return value.trim() === '';
            return !value;
          },
          'none-editable-cell': (params) => params.data?.newYn !== 'Y',
        },
      }
    ),
    CreateColumn(
      'issuSignPath',
      '서명',
      'center',
      160,
      false,
      false,
      false,
      'text',
      {
        minWidth: 120,
        maxWidth: 200,
        autoHeight: true,
        cellRenderer: IssuSignPathCellRenderer,
        cellClass: 'none-editable-cell',
        sortable: false,
        filter: false,
      }
    ),
    CreateColumn(
      'equipNm',
      '보호구명',
      'left',
      160,
      true,
      true,
      false,
      'select',
      {
        minWidth: 120,
        selectOptions: equipOptionsList,
        selectValueKey: 'erCd',
        selectLabelKey: 'equipNm',
        cellRenderer: CreateEquipNmHintCellRenderer(equipOptionsList),
        editableCondition: (params) => {
          if (params.data?.newYn !== 'Y') return false;
          const dep = String(params.data?.userDepCd ?? '').trim();
          if (!dep) return false;
          const allowed = FilterEquipOptionsByUserDepCd(equipOptionsList, dep);
          return allowed.length > 0;
        },
        cellEditorParams: (params) => {
          const allowed = FilterEquipOptionsByUserDepCd(
            equipOptionsList,
            params.data?.userDepCd
          );
          return {
            values: allowed.map((o) => o.equipNm).filter(Boolean),
          };
        },
        valueSetter: (params) => {
          const allowed = FilterEquipOptionsByUserDepCd(
            equipOptionsList,
            params.data?.userDepCd
          );
          const selected = allowed.find(
            (opt) => (opt.equipNm || '') === params.newValue
          );
          if (selected) {
            params.data.equipNm = selected.equipNm;
            params.data.erCd = selected.erCd;
          } else {
            params.data.equipNm = '';
            params.data.erCd = '';
          }
          return true;
        },
        cellClassRules: {
          'none-editable-cell': (params) => {
            if (params.data?.newYn !== 'Y') return true;
            const dep = String(params.data?.userDepCd ?? '').trim();
            if (!dep) return true;
            const allowed = FilterEquipOptionsByUserDepCd(
              equipOptionsList,
              dep
            );
            return allowed.length === 0;
          },
        },
      }
    ),
    CreateColumn(
      'createDate',
      '지급일자',
      'center',
      180,
      true,
      false,
      false,
      'datetime',
      {
        minWidth: 170,
        dateFormat: 'YYYY-MM-DD HH:mm',
        editableCondition: (params) => params.data?.newYn === 'Y',
        cellClassRules: {
          'none-editable-cell': (params) => params.data?.newYn !== 'Y',
        },
      }
    ),
    CreateColumn('qty', '수량', 'center', 100, true, true, false, 'number', {
      minWidth: 80,
      editableCondition: (params) => params.data?.newYn === 'Y',
      cellClassRules: {
        'required-cell': (params) => {
          if (params.data?.newYn !== 'Y') return false;
          const value = params.value;
          if (value === null || value === undefined || value === '')
            return true;
          if (typeof value === 'number') return false;
          if (typeof value === 'string') return value.trim() === '';
          return !value;
        },
        'none-editable-cell': (params) => params.data?.newYn !== 'Y',
      },
      valueFormatter: (params) => {
        const v = params?.value;
        if (v == null || v === '') return '';
        const n = typeof v === 'number' ? v : Number(v);
        return Number.isFinite(n) ? n.toLocaleString('ko-KR') : String(v);
      },
    }),
    CreateColumn(
      'issuUserNm',
      '지급인',
      'center',
      130,
      false,
      true,
      false,
      'requiredButton',
      {
        minWidth: 100,
        buttonClickHandler: (params) => {
          if (params.data?.newYn === 'Y') {
            props.MemberSelectPopupClick1?.(params, 'issuUserNm');
          }
        },
        buttonEmptyText: '설정하기',
        buttonEmptyTooltip: '지급인을 선택하세요.',
        buttonValueTooltip: (params) => {
          const isNew = params.data?.newYn === 'Y';
          return isNew
            ? '클릭하여 지급인을 다시 선택할 수 있습니다.'
            : '조회된 행은 여기서 수정할 수 없습니다.';
        },
        cellClassRules: {
          'required-cell': (params) => {
            if (params.data?.newYn !== 'Y') return false;
            const value = params.value;
            if (typeof value === 'string') return value.trim() === '';
            return !value;
          },
          'none-editable-cell': (params) => params.data?.newYn !== 'Y',
        },
      }
    ),
    CreateColumn('remark', '비고', 'left', 200, true, false, false, 'text', {
      flex: 1,
      minWidth: 160,
    }),
  ];
};

const columnDefs = { getColumnDefs1 };
export default columnDefs;
